import { useMemo } from 'react';
import { last } from 'fp-ts/Array';
import { Alert, Snackbar, SnackbarOrigin } from '@mui/material';
import { styled } from '@customink/pigment-react/lib/themeCustomink';
import { PWC } from '~/adapters/typescript/propsWithChildren';
import { NotificationContext } from '~/contexts/NotificationContext';
import { match } from 'fp-ts/lib/Option';
import { pipe } from 'fp-ts/function';
import { useNotificationState } from './useNotificationState';

const SnackBarAlert = styled(Alert)(({ theme }) => ({
    width: 450,
    borderRadius: theme.shape.borderRadius,
    borderWidth: 2,
}));

const defaultAnchorOrigin: SnackbarOrigin = { horizontal: 'center', vertical: 'top' };

export function SnackbarNotificationProvider({ children }: PWC) {
    // note: unlike Legacy NotificationProvider which uses Flash, only one Snackbar can be displayed at a time
    const [notifications, setNotification] = useNotificationState();
    const providerValue = useMemo(() => ({ pushNotification: setNotification }), []);

    // TODO: handle multiple notifications https://customink.atlassian.net/browse/NT-780
    const notificationNode = pipe(
        notifications,
        last,
        match(
            () => <></>,
            (notification) => (
                <Snackbar open anchorOrigin={notification.anchorOrigin ?? defaultAnchorOrigin} key={notification.id}>
                    <SnackBarAlert severity={notification.type} variant="outlined">
                        {notification.message}
                    </SnackBarAlert>
                </Snackbar>
            ),
        ),
    );

    return (
        <NotificationContext.Provider value={providerValue}>
            {notificationNode}
            {children}
        </NotificationContext.Provider>
    );
}
